import styled from '@emotion/styled';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { decode } from 'html-entities';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { ItemThumbnail, QuantityStepper, VendorLogoBlock } from 'src/components';
import ImgUnavailable from 'src/images/other/unavailable.svg?react';
import { formatPrice, itemOutboundHref } from 'src/utils';
import type { CartAmazonDeal, CartGetItem } from 'src/types';
import { rowDealBasis } from '../deals/amazonCartDeals';
import { AmazonDealBadges } from './AmazonDealBadges';
import { AmazonDealBasis } from './AmazonDealBasis';

type Props = {
  item: CartGetItem;
  ccy: string;
  ccys: string;
  vendor: string;
  compare: boolean;
  locale: string;
  page?: 'get' | 'review' | 'receive' | 'embed' | 'make';
  isMissing?: boolean;
  // Editable-row extensions (make page). All optional; when absent the row
  // renders exactly as before for get/review/receive/embed/compare.
  selected?: boolean;
  onToggleSelect?: () => void;
  onQuantityChange?: (qty: number) => void;
  qtyErrorMessage?: string;
  showQty?: boolean;
  action?: ReactNode;
  deal?: CartAmazonDeal | null;
};

export const CartItem = ({
  item,
  ccy,
  ccys,
  vendor,
  compare,
  locale,
  page = 'get',
  isMissing = false,
  selected = false,
  onToggleSelect,
  onQuantityChange,
  qtyErrorMessage,
  showQty = true,
  action,
  deal,
}: Props) => {
  const [showPrice, setShowPrice] = useState<boolean>(true);
  const [formattedPrice, setFormattedPrice] = useState<string>('');
  const { t } = useTranslation();

  /**
   * if item price doesn't exist
   * OR if item price does exist, is a string, and includes -1
   * OR if item price does exist, is a number, and equals -1
   * then don't show the price
   */
  useEffect(() => {
    if (
      !item.price ||
      (typeof item.price === 'string' && item.price.includes('-1')) ||
      item.price === -1
    ) {
      setShowPrice(false);
    } else {
      const priceInt = typeof item.price === 'number' ? item.price : parseFloat(item.price);
      setShowPrice(true);
      setFormattedPrice(formatPrice(priceInt, locale, ccy, ccys, false));
    }
  }, [ccy, ccys, item.price, locale]);

  const selectable = !!onToggleSelect;
  // The make page stacks until lg (with price/qty flipped); other pages keep
  // their original sm row switch.
  const isMake = page === 'make';
  const itemHref = itemOutboundHref(item.url);
  const title = item.title ? decode(item.title) : item.asin;
  const dealBasisAmount = rowDealBasis(deal, item.price);

  return (
    <li
      onClick={selectable ? onToggleSelect : undefined}
      className={`relative flex w-full items-center justify-center border-b-2 border-gray-200 first:border-t-2 ${
        // Selected (make) / missing (receive) indicator: an overlay strip
        // instead of border-l so the top/bottom borders span the full width
        // and rows reserve no left space.
        (page === 'receive' && isMissing) || selected
          ? 'before:absolute before:inset-y-0 before:left-0 before:w-2 before:bg-red-500'
          : ''
      }`}
    >
      {selectable && (
        <input
          type='checkbox'
          className='primary-checkbox ml-4 shrink-0 cursor-pointer border-2 border-solid border-gray-200 sm:ml-6 lg:ml-[112px]'
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={onToggleSelect}
          aria-label='item select toggle'
        />
      )}
      <div
        className={`flex w-full max-w-[1440px] flex-col items-start justify-center gap-2 xs:flex-row xs:items-center sm:items-center sm:gap-4 md:gap-6 ${
          // When the checkbox column renders, it carries the left inset so it
          // sits close to the image; the wrapper keeps a constant 16px left pad.
          selectable
            ? 'py-4 pr-4 pl-4 sm:py-6 sm:pr-6 lg:pr-[112px]'
            : 'p-4 sm:p-6 lg:px-[112px]'
        }`}
      >
        <div className='flex h-[110px] max-h-[110px] min-h-[110px] w-[110px] min-w-[110px] max-w-[110px] grow-0 items-center justify-center'>
          <ItemThumbnail
            className='h-auto max-h-[110px] w-auto max-w-[110px]'
            src={item.image}
            alt={item.title ? decode(item.title) : 'item'}
            fallback={
              <ImgUnavailable className='h-[110px] max-h-[110px] min-h-[110px] w-[110px] min-w-[110px] max-w-[110px]' />
            }
          />
        </div>
        <div
          className={`flex h-full min-h-[110px] w-full grow flex-col items-start justify-between ${
            isMake
              ? 'gap-4 sm:flex-row sm:items-center sm:gap-4 md:gap-6'
              : 'gap-2 sm:flex-row sm:items-center sm:gap-4 md:gap-6'
          }`}
        >
          <div
            className='flex grow flex-col items-start justify-center sm:w-min'
          >
            <h5 className='heading-200'>
              {itemHref ? (
                <Link
                  to={itemHref}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:underline'
                >
                  {title}
                </Link>
              ) : (
                <span>{title}</span>
              )}
            </h5>
            {(item.skuString || item.sku) && (
              <p className='text-100' style={{ overflowWrap: 'anywhere' }}>
                <b className='font-bold'>SKU:</b>&nbsp;
                {item.skuString || item.sku}
              </p>
            )}
            {deal && <AmazonDealBadges deal={deal} />}
            {!item.title && (
              <MissingDetailsTooltip
                title={
                  <>
                    <p className='font-secondary'>{decode(t('common.missing_details'))}</p>
                  </>
                }
                placement='top'
                arrow
              >
                <div className='group inline-flex items-center justify-center gap-1 fill-primary pt-2 hover:fill-primary/70'>
                  <InfoOutlinedIcon style={{ fill: 'inherit', fontSize: 24 }} />
                  <span className='heading-100 text-primary group-hover:text-primary/70'>
                    {t('common.couldnt_fetch_details')}
                  </span>
                </div>
              </MissingDetailsTooltip>
            )}
          </div>
          <div
            className={`ml-auto flex flex-row flex-nowrap items-center xs:gap-x-6 ${
              isMake ? 'justify-evenly' : 'justify-between'
            } ${
              // Without a qty cell the lone price stays right-aligned.
              showQty ? 'max-sm:ml-0' : ''
            } ${
              // make: qty stacked above price once the row layout kicks in.
              isMake ? 'sm:flex-col sm:items-center sm:gap-2' : 'sm:gap-4 md:gap-6'
            } ${
              showPrice &&
              showQty &&
              (isMake ? 'w-full sm:w-fit' : 'w-full xs:w-fit xs:min-w-[240px]')
            }`}
          >
            {showQty &&
              (onQuantityChange ? (
                <QuantityStepper
                  quantity={item.quantity}
                  onQuantityChange={onQuantityChange}
                  label={t('common.qty')}
                  errorMessage={qtyErrorMessage ?? ''}
                />
              ) : (
                <div className='inline-flex items-center justify-center gap-3'>
                  <span className='text-200 whitespace-nowrap'>{t('common.qty')}</span>
                  <div className='flex h-10 w-[43px] items-center justify-center rounded-lg'>
                    <span className='font-secondary text-base font-semibold leading-[16px] text-primary-dark'>
                      {item.quantity}
                    </span>
                  </div>
                </div>
              ))}
            {/* show on the compare page regardless of status of price */}
            {(showPrice || compare) && (
              <div className='flex flex-col items-end justify-center gap-y-2 lg:flex-row lg:items-center lg:gap-x-2'>
                {compare && item.savingsBool === true
                  ? (() => {
                      const savingsInt = item.savings
                        ? typeof item.savings === 'number'
                          ? item.savings
                          : parseFloat(item.savings)
                        : 0;
                      const formattedSavings = formatPrice(savingsInt, locale, ccy, ccys, false);

                      return (
                        <div className='flex min-w-fit max-w-fit flex-row flex-nowrap items-center justify-center whitespace-nowrap rounded-md bg-customYellow px-2 py-0'>
                          <span className='text-200'>{t('get.save')}&nbsp;</span>
                          <span className='heading-200'>{formattedSavings}</span>
                        </div>
                      );
                    })()
                  : null}
                <div className='flex flex-col items-end'>
                  {dealBasisAmount !== null && (
                    <AmazonDealBasis
                      amount={dealBasisAmount}
                      locale={locale}
                      ccy={ccy}
                      ccys={ccys}
                    />
                  )}
                  <div className='flex flex-row flex-nowrap items-center justify-center gap-1'>
                    {compare && <VendorLogoBlock vendor={vendor} size='medium' />}
                    <span className='heading-200 text-end'>{formattedPrice}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {action && (
            <div
              className='w-full shrink-0 sm:w-auto sm:min-w-[140px]'
              onClick={(e) => e.stopPropagation()}
            >
              {action}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

const MissingDetailsTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#F1F1F1',
    color: '#04033C',
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#3032DC',
    padding: 16,
    fontFamily: 'Open Sans',
    marginInline: 4,
    marginTop: '6px !important',
    marginBottom: '0px !important',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#F1F1F1',
    fontSize: 20,
    '&:before': {
      border: '2px solid #3032DC',
    },
  },
  [`& .${tooltipClasses.popper}`]: {
    color: '#F1F1F1',
  },
  [`& .${tooltipClasses.popperArrow}`]: {
    color: '#F1F1F1',
  },
}));
